import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type EmployeeNotificationPrefs = {
  lowStockAlertsEnabled: boolean;
  saleConfirmationEnabled: boolean;
};

const DEFAULT_PREFS: EmployeeNotificationPrefs = {
  lowStockAlertsEnabled: true,
  saleConfirmationEnabled: true,
};

export function useEmployeeNotificationPrefs() {
  const { user, businessId, profile } = useAuth();
  const resolvedBusinessId = businessId ?? profile?.business_id ?? null;
  const [prefs, setPrefs] = useState<EmployeeNotificationPrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);

  const fetchPrefs = useCallback(async () => {
    if (!user?.id || !resolvedBusinessId) {
      setPrefs(DEFAULT_PREFS);
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from("notification_preferences")
      .select("low_stock_enabled, sale_confirmation_enabled")
      .eq("user_id", user.id)
      .eq("business_id", resolvedBusinessId)
      .maybeSingle();

    if (error || !data) {
      setPrefs(DEFAULT_PREFS);
      setLoading(false);
      return;
    }

    setPrefs({
      lowStockAlertsEnabled: data.low_stock_enabled ?? true,
      saleConfirmationEnabled: (data as { sale_confirmation_enabled?: boolean }).sale_confirmation_enabled ?? true,
    });
    setLoading(false);
  }, [resolvedBusinessId, user?.id]);

  useEffect(() => {
    fetchPrefs();
  }, [fetchPrefs]);

  const savePrefs = useCallback(
    async (updates: Partial<EmployeeNotificationPrefs>) => {
      if (!user?.id || !resolvedBusinessId) return;

      const next = { ...prefs, ...updates };
      setPrefs(next);

      const { error } = await supabase.from("notification_preferences").upsert(
        {
          business_id: resolvedBusinessId,
          user_id: user.id,
          low_stock_enabled: next.lowStockAlertsEnabled,
          sale_confirmation_enabled: next.saleConfirmationEnabled,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "business_id,user_id" },
      );

      if (error) throw error;
    },
    [prefs, resolvedBusinessId, user?.id],
  );

  return {
    prefs,
    loading,
    savePrefs,
    refetch: fetchPrefs,
  };
}
